import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LoadingService } from './loading.service';
import { ToastService } from './toast.service';
import { environment as env } from '../../environments/environment';

/**
 * Customer-asset API client. Wraps the f2-admin-service2
 * /rest/admin/customer-assets endpoints (F2-ADMIN.CustomerAssets +
 * F2-ADMIN.CustomerAssetsAudit). Assets are integration docs
 * (HTML / PDF / MD) attached per Customer; share-tokens produce
 * shareable URLs that still require an F2 login to redeem.
 *
 * Auth: same cookie-based F2 session as the rest of the admin SPA
 * (withCredentials: true). All write paths are admin-role gated on
 * the server side; the UI mirrors that gate per-section.
 *
 * Pattern matches customer-directory.service.ts: HttpClient +
 * env.apiUrl, Promise-returning (consumers `await` instead of
 * subscribe), toast on failure, loading-service id stack so the
 * top-of-screen progress bar reflects in-flight work.
 */

// Shape returned by the list / upload / share endpoints. share_tokens[]
// only ever exposes the sha256 hash here — the plaintext token is
// returned exactly once at create-share time.
export interface AssetSummary {
  id: string;
  customer_slug: string;
  filename: string;
  content_type: string;
  size: number;
  label: string;
  category: string;
  visibility: ('admin' | 'client_admin' | 'member')[];
  uploaded_at: string;
  uploaded_by_email: string;
  share_tokens: Array<{
    token_hash: string;
    label: string;
    created_at: string;
    created_by_email: string;
    expires_at: string | null;
    revoked_at: string | null;
    revoked_by_email: string | null;
    active: boolean;
  }>;
}

export interface AssetAuditRow {
  id: string;
  asset_id: string;
  customer_slug: string;
  action: 'upload' | 'delete' | 'share_create' | 'share_revoke' | 'view';
  actor_email: string;
  at: string;
  details: Record<string, any>;
  signature: string;
}

export interface AssetShareCreateResponse {
  token: string;       // plaintext — only returned here, never again
  share_url: string;   // relative path; full URL = env.apiUrl + share_url
  entry: {
    label: string;
    created_at: string;
    created_by_email: string;
    expires_at: string | null;
  };
}

export type AssetVisibility = 'admin' | 'client_admin' | 'member';

@Injectable({ providedIn: 'root' })
export class CustomerAssetsService {
  // Mirrors the server-side controller cap (700 KB binary). Used by the
  // UI to short-circuit oversize uploads before sending a payload that
  // would 413 server-side. Server-side check is still authoritative.
  static readonly MAX_BYTES = 700 * 1024;

  constructor(
    private http: HttpClient,
    private loadingService: LoadingService,
    private toast: ToastService
  ) {}

  private _opts() {
    return { withCredentials: true };
  }

  private _base(): string {
    return `${env.apiUrl}/rest/admin/customer-assets`;
  }

  /** GET /:customer → AssetSummary[] (filtered server-side to assets the requester can see). */
  async list(customer: string): Promise<AssetSummary[]> {
    const id = this.loadingService.add('Loading customer assets');
    const url = `${this._base()}/${encodeURIComponent(customer)}`;
    try {
      const res: any = await this.http
        .get(url, this._opts())
        .toPromise();
      this.loadingService.remove(id);
      if (res?.err) {
        this.toast.error('Customer assets', res.err, {
          error_code: 'F2A_ASSETS_LIST_FAIL',
          customer,
          scanner_path: '/admin/customer-assets',
          request_url: url,
          http_status: 200,
          f2_response_body: JSON.stringify(res).slice(0, 500),
          f2_response_message: String(res.err),
        });
        return [];
      }
      return Array.isArray(res) ? (res as AssetSummary[]) : [];
    } catch (e: any) {
      this.loadingService.remove(id);
      const msg = e?.error?.errors?.[0]?.message || e?.message || 'Unknown error';
      this.toast.error('Customer assets', msg, {
        error_code: 'F2A_ASSETS_LIST_FAIL',
        customer,
        scanner_path: '/admin/customer-assets',
        request_url: e?.url || url,
        http_status: e?.status ?? 0,
        content_type: (e?.headers?.get?.('content-type') as string | undefined) || 'unknown',
        f2_response_body: (typeof e?.error === 'string' ? e.error : JSON.stringify(e?.error || {})).slice(0, 500),
        f2_response_message: msg,
      });
      return [];
    }
  }

  /** GET / → AssetSummary[] across every customer the requester can read.
   *  Used by the standalone /admin/customer-assets panel. Server applies
   *  the same role + customer + visibility filter as the per-customer list. */
  async listAll(): Promise<AssetSummary[]> {
    const id = this.loadingService.add('Loading customer assets');
    const url = this._base();
    try {
      const res: any = await this.http
        .get(url, this._opts())
        .toPromise();
      this.loadingService.remove(id);
      if (res?.err) {
        this.toast.error('Customer assets', res.err, {
          error_code: 'F2A_ASSETS_LISTALL_FAIL',
          customer: 'f2-admin',
          scanner_path: '/admin/customer-assets',
          request_url: url,
          http_status: 200,
          f2_response_body: JSON.stringify(res).slice(0, 500),
          f2_response_message: String(res.err),
        });
        return [];
      }
      return Array.isArray(res) ? (res as AssetSummary[]) : [];
    } catch (e: any) {
      this.loadingService.remove(id);
      const msg = e?.error?.errors?.[0]?.message || e?.message || 'Unknown error';
      // Stamp both the URL Angular ATTEMPTED (e.url — includes any post-
      // redirect / normalization) and the URL WE composed (url). If they
      // differ, that's the trailing-slash / rewrite-miss signature the
      // toast in Mike's 2026-07-08 screenshot showed — the ER will
      // reveal it without another round of guesswork.
      this.toast.error('Customer assets', msg, {
        error_code: 'F2A_ASSETS_LISTALL_FAIL',
        customer: 'f2-admin',
        scanner_path: '/admin/customer-assets',
        request_url: e?.url || url,
        composed_url: url,
        http_status: e?.status ?? 0,
        content_type: (e?.headers?.get?.('content-type') as string | undefined) || 'unknown',
        f2_response_body: (typeof e?.error === 'string' ? e.error : JSON.stringify(e?.error || {})).slice(0, 500),
        f2_response_message: msg,
      });
      return [];
    }
  }

  /**
   * Build the direct-serve URL for an asset. The browser hits this
   * directly (e.g. `<a [href]>` with target="_blank" or window.open)
   * so the response can stream as the correct content-type with
   * inline disposition. Cookie auth is sent automatically by the
   * browser; no Authorization header involved.
   */
  serveUrl(customer: string, assetId: string): string {
    return `${this._base()}/${encodeURIComponent(customer)}/${encodeURIComponent(assetId)}`;
  }

  /** Build the public share-link URL the operator copies to send to the customer. */
  shareUrl(token: string): string {
    return `${this._base()}/asset-share/${token}`;
  }

  /**
   * POST /  Upload a new asset. bytes_base64 must be the RAW base64
   * payload — strip the `data:<mime>;base64,` prefix before calling.
   * SPA-side size guard so oversize files don't burn the round trip.
   */
  async upload(params: {
    customer_slug: string;
    filename: string;
    content_type: string;
    label: string;
    category: string;
    visibility: AssetVisibility[];
    bytes_base64: string;
  }): Promise<AssetSummary | { err: string }> {
    // Approximate the decoded binary size (base64 grows ~33%; trailing '='
    // padding doesn't count toward bytes). Cheap pre-flight only — the
    // server's PayloadTooLarge guard is authoritative.
    const approxBytes = Math.floor(params.bytes_base64.replace(/=+$/, '').length * 0.75);
    if (approxBytes > CustomerAssetsService.MAX_BYTES) {
      const msg = `Asset exceeds ${Math.floor(CustomerAssetsService.MAX_BYTES / 1024)} KB binary cap (got ~${Math.floor(approxBytes / 1024)} KB).`;
      this.toast.error('Upload too large', msg);
      return { err: msg };
    }
    if (!params.visibility?.length) {
      const msg = 'Select at least one visibility role before uploading.';
      this.toast.error('Visibility required', msg);
      return { err: msg };
    }

    const id = this.loadingService.add('Uploading asset');
    try {
      const res: any = await this.http
        .post(this._base(), params, this._opts())
        .toPromise();
      this.loadingService.remove(id);
      if (res?.err) {
        this.toast.error('Upload failed', res.err);
        return res;
      }
      this.toast.success('Asset uploaded', params.filename);
      return res as AssetSummary;
    } catch (e: any) {
      this.loadingService.remove(id);
      const msg = e?.error?.errors?.[0]?.message || e?.error?.message || e?.message;
      this.toast.error('Upload failed', msg);
      return { err: msg };
    }
  }

  /** DELETE /:asset_id  Admin-only on the server side. */
  async delete(assetId: string): Promise<{ ok: boolean; err?: string }> {
    const id = this.loadingService.add('Deleting asset');
    try {
      const res: any = await this.http
        .delete(`${this._base()}/${encodeURIComponent(assetId)}`, this._opts())
        .toPromise();
      this.loadingService.remove(id);
      if (res?.err) {
        this.toast.error('Delete failed', res.err);
        return { ok: false, err: res.err };
      }
      this.toast.success('Asset deleted', assetId.slice(0, 8) + '…');
      return { ok: true };
    } catch (e: any) {
      this.loadingService.remove(id);
      const msg = e?.error?.errors?.[0]?.message || e?.message;
      this.toast.error('Delete failed', msg);
      return { ok: false, err: msg };
    }
  }

  /**
   * POST /:asset_id/share  Returns the plaintext token EXACTLY ONCE.
   * UI MUST surface + copy it now; subsequent list reads only see the
   * sha256 hash. expires_at is optional ISO timestamp (null → never).
   */
  async createShare(
    assetId: string,
    body: { label: string; expires_at?: string | null }
  ): Promise<AssetShareCreateResponse | { err: string }> {
    const id = this.loadingService.add('Creating share link');
    try {
      const res: any = await this.http
        .post(`${this._base()}/${encodeURIComponent(assetId)}/share`, body, this._opts())
        .toPromise();
      this.loadingService.remove(id);
      if (res?.err) {
        this.toast.error('Share failed', res.err);
        return res;
      }
      this.toast.success('Share link created', body.label || 'unlabeled');
      return res as AssetShareCreateResponse;
    } catch (e: any) {
      this.loadingService.remove(id);
      const msg = e?.error?.errors?.[0]?.message || e?.message;
      this.toast.error('Share failed', msg);
      return { err: msg };
    }
  }

  /** POST /:asset_id/share/:token_hash/revoke  Server matches by sha256 hash (the
   * stable public identifier from the list response). Idempotent-ish (404 if
   * already revoked or never existed). */
  async revokeShare(assetId: string, tokenHash: string): Promise<{ ok: boolean; err?: string }> {
    const id = this.loadingService.add('Revoking share link');
    try {
      const res: any = await this.http
        .post(
          `${this._base()}/${encodeURIComponent(assetId)}/share/${encodeURIComponent(tokenHash)}/revoke`,
          {},
          this._opts()
        )
        .toPromise();
      this.loadingService.remove(id);
      if (res?.err) {
        this.toast.error('Revoke failed', res.err);
        return { ok: false, err: res.err };
      }
      this.toast.success('Share link revoked', '');
      return { ok: true };
    } catch (e: any) {
      this.loadingService.remove(id);
      const msg = e?.error?.errors?.[0]?.message || e?.message;
      this.toast.error('Revoke failed', msg);
      return { ok: false, err: msg };
    }
  }

  /** GET /:customer/:asset_id/audit  Admin-only. HMAC signature shown raw — no client-side verification. */
  async audit(customer: string, assetId: string): Promise<AssetAuditRow[]> {
    const id = this.loadingService.add('Loading audit trail');
    try {
      const res: any = await this.http
        .get(
          `${this._base()}/${encodeURIComponent(customer)}/${encodeURIComponent(assetId)}/audit`,
          this._opts()
        )
        .toPromise();
      this.loadingService.remove(id);
      if (res?.err) {
        this.toast.error('Audit load failed', res.err);
        return [];
      }
      return Array.isArray(res) ? (res as AssetAuditRow[]) : [];
    } catch (e: any) {
      this.loadingService.remove(id);
      const msg = e?.error?.errors?.[0]?.message || e?.message;
      this.toast.error('Audit load failed', msg);
      return [];
    }
  }
}
